// eslint-disable-next-line import/no-cycle
import { isEmpty, isNil, sortBy } from 'lodash';
import { HIGH_VOLUME_IMPORT_VEHICLE } from 'utils/constant';
import { formatDate, parseTime, price, timestamp } from 'utils/helpers';
import { luggageTypeRegex } from 'tenants/sealink/ferry_flow/luggage_form/helpers';

export const getServices = ({
  tripId,
  fromRouteStopId,
  toRouteStopId,
  currentFerryInGrouping,
}) => {
  const flattenFerryInGrouping = Object.values(currentFerryInGrouping).flat();
  const foundService = flattenFerryInGrouping.find(
    (service) =>
      service.trip_id === tripId &&
      service.from_route_stop_attributes?.id === fromRouteStopId &&
      service.to_route_stop_attributes?.id === toRouteStopId
  );

  if (!foundService) {
    return null;
  }

  return {
    departure: {
      name: foundService?.from_route_stop_attributes.name,
      departure_time: parseTime(foundService?.stop_departure_time || ''),
      // Return raw time for sortings
      raw_time: foundService?.stop_departure_time,
    },
    arrival: {
      name: foundService?.to_route_stop_attributes.name,
      arrival_time: parseTime(foundService?.stop_arrival_time || ''),
      // Return raw time for sortings
      raw_time: foundService?.stop_arrival_time,
    },
  };
};

export const getVehicleTypeName = (currentVehicle, cmsVehicleTypes) => {
  const vehicleTypeId =
    currentVehicle.vehicleTypeId || currentVehicle.vehicle_type_id;
  const vehicleType = cmsVehicleTypes.find(
    (item) => item.qt_vehicle_type_id === vehicleTypeId
  );

  return vehicleType?.name;
};

const reg = /(motorhome|motorbike|truck)/i;
export const getVehicleDescription = ({
  currentVehicle,
  vehicleMakes,
  vehicleFamilies,
  cmsVehicleTypes,
  qtVehicleTypes,
}) => {
  const vehicleTypeName = getVehicleTypeName(currentVehicle, cmsVehicleTypes);

  if (reg.test(vehicleTypeName)) {
    return vehicleTypeName;
  }

  const vehicleTypeId =
    currentVehicle.vehicleTypeId || currentVehicle.vehicle_type_id;
  const segmentCode = qtVehicleTypes.find(
    (item) => item.id === Number(vehicleTypeId)
  )?.segment_code;

  const vehicleMake = currentVehicle.vehicleMake || currentVehicle.make_code;
  const vehicleModel =
    currentVehicle.vehicleModel || currentVehicle.family_code;

  if (
    vehicleMake === HIGH_VOLUME_IMPORT_VEHICLE ||
    vehicleModel === HIGH_VOLUME_IMPORT_VEHICLE
  ) {
    return 'Vehicle';
  }

  if (
    !vehicleMake ||
    !vehicleMakes[segmentCode] ||
    !vehicleFamilies[`${segmentCode}_${vehicleMake}`]
  ) {
    const vehicleType = cmsVehicleTypes.find(
      (vehicle_type) =>
        // In vehicle form, if the vehicle is a hired car, vehicle category has to be selected,
        // vehicle category is the id of the vehicle type entry in the cms,
        // So we can find the vehicle type name base on vehicle category.
        (currentVehicle.vehicleCategory &&
          vehicle_type.id === Number(currentVehicle.vehicleCategory)) ||
        // From vehicle_attribute of a booking object, we have vehicle_type_id and vehicle length,
        // As the qt_vehicle_type_id is not unique in the cms, so we need to check the length to
        // find the vehicle type name from cms
        (currentVehicle.vehicle_type_id &&
          currentVehicle.length &&
          vehicle_type.qt_vehicle_type_id === currentVehicle.vehicle_type_id &&
          currentVehicle.length === vehicle_type.dimensions?.length)
    );

    return (
      vehicleType?.name ||
      currentVehicle.vehicleDescription ||
      currentVehicle.details ||
      'Vehicle'
    );
  }

  const makeDesc = vehicleMakes[segmentCode].find(
    (vehicle) => vehicle.code === vehicleMake
  ).description;
  const familyDesc = vehicleFamilies[`${segmentCode}_${vehicleMake}`].find(
    (family) => family.code === vehicleModel
  ).description;

  return `${makeDesc} ${familyDesc}`;
};

// Rename some products to display
export const getProductName = (productType) => {
  const nameMap = {
    // Coach
    'mainland coach transfers': 'SeaLink Connection',
    // Ferry
    ferry: 'SeaLink Ferry',
  };

  const { name } = productType;
  const keyName = name.toLowerCase();

  return nameMap[keyName] ? nameMap[keyName] : name;
};

export const getProductsNew = ({
  departureServiceForm,
  returnServiceForm,
  qtProductTypes,
}) => {
  const result = {};
  [departureServiceForm, returnServiceForm].forEach((serviceForm, index) => {
    if (!isEmpty(serviceForm)) {
      const values = Object.values(serviceForm).filter(
        (item) => !item.noRequireService && item.productTypeId
      );
      values.sort((a, b) => a.index - b.index);
      const key = index === 0 ? 'departureProduct' : 'returnProduct';
      result[key] = values.map(
        ({
          productTypeId,
          fromRouteStopName,
          toRouteStopName,
          departureTime,
          arrivalTime,
        }) => {
          const label = getProductName(qtProductTypes[productTypeId]);
          return {
            label,
            departure: {
              name: fromRouteStopName,
              departure_time: parseTime(departureTime),
            },
            arrival: {
              name: toRouteStopName,
              arrival_time: parseTime(arrivalTime),
            },
          };
        }
      );
    }
  });
  return result;
};

// Doc for font awesome prefix - https://fontawesome.com/v5/docs/web/reference-icons/
export const getVehicleIcon = (vehicle, iconPrefix, vehicleTypeInfo) => {
  const id = vehicle.vehicleTypeId || vehicle.vehicle_type_id;
  const { vehicle_type_groups, vehicle_types } = vehicleTypeInfo;
  const vehicleTypeDetail = vehicle_types.find(
    (type) => type.qt_vehicle_type_id === id
  );
  if (!vehicleTypeDetail) return undefined;
  const groupDetail = vehicle_type_groups.find((group) =>
    [...group.vehicle_types, ...group.hire_vehicle_types].includes(
      vehicleTypeDetail.id
    )
  );

  if (groupDetail) {
    return `${iconPrefix} ${groupDetail.icon}`;
  }
};

export const getSegment = (reservationRouteId, segments) =>
  Object.values(segments).find(
    (segment) => segment.qt_route_id === reservationRouteId
  );

export const getSegmentDetail = (reservation, qtProductTypes) => {
  const productType = qtProductTypes[reservation.product_type_id];

  return {
    departure: {
      name: reservation.from_route_stop_attributes.name,
      departure_time: parseTime(reservation.start_time),
    },
    arrival: {
      name: reservation.to_route_stop_attributes.name,
      arrival_time: parseTime(reservation.end_time),
    },
    label: getProductName(productType),
  };
};

export const getSegmentDetailWithDate = (reservation, qtProductTypes) => {
  const result = getSegmentDetail(reservation, qtProductTypes);
  return { ...result, date: formatDate(reservation.first_travel_date._value) };
};

export const isOnJourneyPage = (location) =>
  location.pathname.endsWith('bookings/ferry');

export const sortByStartTime = (a, b) =>
  timestamp(a.start_time) - timestamp(b.start_time);

export const sortByDateAndTime = (a, b) => {
  const dateA = timestamp(a.first_travel_date._value);
  const dateB = timestamp(b.first_travel_date._value);
  if (dateA !== dateB) {
    return dateA - dateB;
  }
  return sortByStartTime(a, b);
};

export const getLuggageSummaryFromBooking = ({ vehicles, luggageTypes }) => {
  const counter = (vehicles || []).reduce((accum, currentVal) => {
    const { vehicle_type_id: id } = currentVal;
    if (accum[id] === undefined) {
      accum[id] = 1;
    } else {
      accum[id] += 1;
    }
    return accum;
  }, {});
  return sortBy(Object.keys(counter)).map((id) => ({
    count: counter[id],
    name: luggageTypes.find((item) => item.id === Number(id))?.name,
  }));
};

export const getLuggageSummaryFromForm = ({ formValues, luggageTypes }) =>
  sortBy(Object.entries(formValues || {}), 0).reduce(
    (accumulator, [key, count]) => {
      if (luggageTypeRegex.test(key) && count > 0) {
        const id = Number(key.match(luggageTypeRegex)[1]);
        const name = luggageTypes.find((item) => item.id === id)?.name;
        accumulator = accumulator.concat({ name, count });
      }
      return accumulator;
    },
    []
  );

// Sometimes we have trailers linked to a main vehicle, so we need to group them together.
// Same is the case of truck with different return weight;
const groupVehicles = (vehicleAttributes) => {
  const resultArray = [];
  const vehiclesWithReg = vehicleAttributes.filter((item) => item.registration);
  // Among vehiclesWithReg, there are some trucks with same registration due to the feature of different return weight.
  // So we need to remove the duplicated trucks.

  const surveyResult = vehiclesWithReg.reduce((acc, cur) => {
    acc[cur.registration] = acc[cur.registration] || [];
    acc[cur.registration].push(cur);
    return acc;
  }, {});
  // Let's find out which registrations appear twice.
  Object.values(surveyResult).forEach((value) => {
    if (value.length > 1) {
      resultArray.push({
        id: value[0].id,
        includes: value.map(({ id }) => id),
      });
    }
  });

  const duplicatedTruckIds = resultArray.map(({ includes }) => includes).flat();
  const vehiclesWithoutDuplicatedTruck = vehicleAttributes.filter(
    ({ id }) => !duplicatedTruckIds.includes(id)
  );

  const trailers = vehiclesWithoutDuplicatedTruck.filter(
    ({ linked_to }) => !isNil(linked_to)
  );
  const mainVehicles = vehiclesWithoutDuplicatedTruck.filter(({ linked_to }) =>
    isNil(linked_to)
  );
  mainVehicles.forEach((mainVehicle) => {
    const linkedTrailerIds = trailers
      .filter(({ linked_to }) => linked_to === mainVehicle.id)
      .map(({ id }) => id);
    resultArray.push({
      id: mainVehicle.id,
      includes: [mainVehicle.id, ...linkedTrailerIds],
      linkedTrailerIds,
    });
  });
  return resultArray;
};

export const getVehiclesInfo = ({
  vehicleAttributes,
  ferryReservations,
  vehicleMakes,
  vehicleFamilies,
  cmsVehicleTypes,
  cmsVehicleTypeGroups,
  qtVehicleTypes,
  attachments,
}) => {
  const consumerGrossesArray = ferryReservations.map(
    (item) => item.consumer_grosses
  );

  const groupedVehicles = groupVehicles(vehicleAttributes);
  return groupedVehicles.map(({ id, includes, linkedTrailerIds }) => {
    const currentVehicle = vehicleAttributes.find((item) => item.id === id);
    const name = getVehicleDescription({
      currentVehicle,
      vehicleMakes,
      vehicleFamilies,
      cmsVehicleTypes,
      qtVehicleTypes,
    });
    // Currently we only support one trailer for each vehicle.
    let trailer = null;
    if (!isEmpty(linkedTrailerIds)) {
      const linkedTrailerId = linkedTrailerIds[0];
      const trailerItem = vehicleAttributes.find(
        (item) => item.id === linkedTrailerId
      );
      const cmsVehicleType = cmsVehicleTypes.find(
        (vehicle_type) =>
          vehicle_type.qt_vehicle_type_id === trailerItem.vehicle_type_id
      );
      trailer = `+ ${cmsVehicleType?.name || 'Trailer'} (${
        trailerItem.length
      }m, ${trailerItem.height}m)`;
    }

    const vehicleAttachments = [];
    if (currentVehicle.attachments.length && attachments.length) {
      currentVehicle.attachments.forEach((attachment) => {
        const detail = attachments.find(
          (item) => item.code === attachment.code
        );

        if (detail.category_id === 'roof') {
          vehicleAttachments.push({
            name: `+ Roof luggage (${detail.description})`,
          });
        } else {
          vehicleAttachments.push({ name: `+ ${detail.description}` });
        }
      });
    }

    const icon = getVehicleIcon(currentVehicle, 'fas', {
      vehicle_type_groups: cmsVehicleTypeGroups,
      vehicle_types: cmsVehicleTypes,
    });

    const priceResult = consumerGrossesArray.reduce((acc, consumerGrosses) => {
      includes.forEach((i) => {
        acc += consumerGrosses[i] || 0;
      });
      return acc;
    }, 0);
    return {
      id,
      name,
      trailer,
      attachments: vehicleAttachments,
      icon,
      length: currentVehicle.length,
      year: currentVehicle.year_code,
      price: price(priceResult),
    };
  });
};
